import { Link } from "react-router-dom";
import { ArrowRight, ArrowUp, ArrowDown, Equal, GraduationCap, Wallet, Info, Users } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { translateEducation } from "@/lib/i18n";
import ShareSeatButton from "@/components/ShareSeatButton";
import { MAP_PARTY_COLORS, NO_DATA_COLOR, OUTCOME_COLORS, formatRupees, partyLabel } from "@/lib/insights";

function MlaColumn({ side, label, testid }) {
  const { t, lang } = useLang();
  const ta = lang === "ta";
  if (!side) {
    return (
      <div className="flex-1 rounded-2xl bg-[#f6f3ea] p-4" data-testid={testid}>
        <div className={`text-[11px] uppercase text-[#8c89ad] font-semibold ${ta ? "font-tamil" : "tracking-[0.18em]"}`}>{label}</div>
        <div className={`mt-2 text-sm text-[#6b6894] ${ta ? "font-tamil" : ""}`}>{t("not_listed")}</div>
      </div>
    );
  }
  const color = MAP_PARTY_COLORS[side.party_short] || NO_DATA_COLOR;
  return (
    <div className="flex-1 min-w-0 rounded-2xl bg-[#f6f3ea] p-4" style={{ borderTop: `4px solid ${color}` }} data-testid={testid}>
      <div className={`text-[11px] uppercase text-[#8c89ad] font-semibold ${ta ? "font-tamil" : "tracking-[0.18em]"}`}>{label}</div>
      <div className={`mt-1.5 text-[17px] font-semibold text-[#1a1640] leading-tight break-words ${ta ? "font-tamil" : "font-display"}`}>
        {ta ? side.mla_ta : side.mla}
      </div>
      <div className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] font-bold text-[#1a1640]">
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
        <span className={ta ? "font-tamil" : ""}>{partyLabel(side.party_short, lang)}</span>
      </div>
      <dl className={`mt-3 space-y-1 text-[12.5px] ${ta ? "font-tamil" : ""}`}>
        <Row k={t("assets_label")} v={formatRupees(side.assets, t)} />
        <Row k={t("education")} v={translateEducation(side.education, lang)} />
        <Row k={t("cases_label")} v={side.cases ?? "—"} />
      </dl>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-2">
    <dt className="text-[#6b6894]">{k}</dt>
    <dd className="font-semibold text-[#1a1640] text-right">{v}</dd>
  </div>
);

const VERDICT_ICON = { more: ArrowUp, wealthier: ArrowUp, less: ArrowDown, poorer: ArrowDown, same: Equal };
const VERDICT_TONE = {
  more: "bg-emerald-50 text-emerald-800", less: "bg-rose-50 text-rose-800",
  wealthier: "bg-amber-50 text-amber-900", poorer: "bg-sky-50 text-sky-900",
  same: "bg-slate-100 text-slate-700", na: "bg-slate-100 text-slate-500",
};

function Verdict({ icon: Icon, verdict, text, extra, testid }) {
  const { lang } = useLang();
  const Arrow = VERDICT_ICON[verdict];
  return (
    <div className={`flex items-start gap-3 rounded-2xl px-4 py-3 ${VERDICT_TONE[verdict]}`} data-testid={testid}>
      <Icon size={18} className="mt-0.5 shrink-0" />
      <div className={`text-[13.5px] font-semibold leading-snug ${lang === "ta" ? "font-tamil" : ""}`}>
        <span className="inline-flex items-center gap-1">{Arrow && <Arrow size={14} strokeWidth={3} />}{text}</span>
        {extra && <div className="mt-0.5 text-[12px] font-medium opacity-80">{extra}</div>}
      </div>
    </div>
  );
}

function wealthExtra(c, t) {
  if (c.assets_diff === null || c.assets_diff === undefined) return null;
  const diff = (c.assets_diff > 0 ? "+" : "") + formatRupees(c.assets_diff, t);
  return c.assets_ratio ? `${diff} (${c.assets_ratio}×)` : diff;
}

export default function SeatTransition({ seat, showLink = false }) {
  const { t, lang } = useLang();
  const ta = lang === "ta";
  const c = seat.compare;
  const self = seat.outcome_key === "same_mla" || seat.outcome_key === "same_mla_switched";
  const sfx = self ? "_self" : "";
  const eduKey = c.education === "na" ? "edu_na" : `edu_${c.education}${sfx}`;
  const wealthKey = c.wealth === "na" ? "wealth_na" : `wealth_${c.wealth}${sfx}`;
  const fate = seat.prev_fate;

  return (
    <section className="bg-white rounded-[22px] p-5 sm:p-6 card-in" data-testid="seat-transition">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className={`text-[11px] uppercase text-[#8c89ad] font-semibold ${ta ? "font-tamil" : "tracking-[0.2em]"}`}>
            {t("transition_title")}
          </div>
          <h3 className={`mt-1 text-2xl font-semibold text-[#1a1640] ${ta ? "font-tamil" : "font-display"}`} data-testid="seat-transition-name">
            {ta ? seat.name_ta : seat.name}
          </h3>
          <div className={`text-[12px] text-[#6b6894] ${ta ? "font-tamil" : ""}`}>AC {seat.ac_no} · {ta ? seat.district_ta : seat.district}</div>
        </div>
        <span
          className={`rounded-full px-3 py-1 text-[12px] font-bold text-white ${ta ? "font-tamil" : ""}`}
          style={{ background: OUTCOME_COLORS[seat.outcome_key], color: seat.outcome_key === "not_compared" ? "#1a1640" : "#fff" }}
          data-testid="seat-outcome-badge"
        >
          {t(`outcome_${seat.outcome_key}`)}
        </span>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row items-stretch gap-3">
        <MlaColumn side={seat.y2021} label={t("mla_2021")} testid="seat-mla-2021" />
        <div className="flex items-center justify-center text-[#8c89ad]"><ArrowRight className="rotate-90 sm:rotate-0" size={20} /></div>
        <MlaColumn side={seat.y2026} label={t("mla_2026")} testid="seat-mla-2026" />
      </div>

      {seat.y2021 && seat.y2026 && (
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <Verdict icon={GraduationCap} verdict={c.education} text={t(eduKey)} extra={c.education === "na" ? t("edu_na_note") : null} testid="seat-edu-verdict" />
          <Verdict icon={Wallet} verdict={c.wealth} text={t(wealthKey)} extra={wealthExtra(c, t)} testid="seat-wealth-verdict" />
        </div>
      )}

      <div className={`mt-4 space-y-1.5 text-[12.5px] text-[#6b6894] ${ta ? "font-tamil" : ""}`}>
        {fate?.key === "not_returning" && <p data-testid="seat-prev-fate"><Users size={13} className="inline mr-1" />{t("fate_not_returning")}</p>}
        {fate?.key === "moved" && <p data-testid="seat-prev-fate"><Users size={13} className="inline mr-1" />{t("fate_moved").replace("{seat}", fate.seat)}</p>}
        {seat.match_confidence === "Probable" && (
          <p data-testid="seat-match-note"><Info size={13} className="inline mr-1" />{t("match_probable")} {(ta && seat.match_note_ta) || seat.match_note}</p>
        )}
      </div>

      <div className="flex flex-wrap items-start gap-x-3">
      {(seat.y2021 || seat.y2026) && (
        <ShareSeatButton seat={seat} eduText={t(eduKey)} wealthText={t(wealthKey)} wealthExtra={wealthExtra(c, t)} fateText={fate?.key === "not_returning" ? t("fate_not_returning") : fate?.key === "moved" ? t("fate_moved").replace("{seat}", fate.seat) : null} />
      )}
      {showLink && seat.tn1_name && (
        <Link
          to={`/c/${encodeURIComponent(seat.tn1_name)}`}
          className={`mt-4 inline-flex items-center gap-2 rounded-full bg-[#1a1640] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#2a2170] transition-colors ${ta ? "font-tamil" : ""}`}
          data-testid="seat-view-candidates-link"
        >
          {t("view_candidates")} <ArrowRight size={14} />
        </Link>
      )}
      </div>
    </section>
  );
}
